import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, MessageSquare, Send, MapPin, Award, Sparkles } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { Card, Chip, StatusDot, Button, CompatibilityRing, VerifiedBadge } from "../components/Primitives";
import { getProducersByIds } from "../lib/db";

function Row({ label, values, mono = false }) {
  return (
    <div className="grid grid-cols-[140px_repeat(var(--cols),1fr)] items-center gap-3 py-3 border-b border-carbon-600" style={{ "--cols": values.length }}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-600">{label}</div>
      {values.map((v, i) => (
        <div key={i} className={`text-sm text-ink-900 ${mono ? "font-mono" : ""}`}>{v}</div>
      ))}
    </div>
  );
}

export default function Compare() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ids = (params.get("ids") || "").split(",").filter(Boolean);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getProducersByIds(ids).then((rows) => {
      if (!alive) return;
      const ordered = ids.map((id) => rows.find((r) => r.id === id)).filter(Boolean);
      setCandidates(ordered);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, [params]);

  if (loading) {
    return <div className="p-8 max-w-3xl mx-auto text-ink-500 font-mono text-xs">Loading…</div>;
  }

  if (candidates.length === 0) {
    return (
      <div className="p-8 max-w-3xl mx-auto animate-fade-up">
        <button onClick={() => navigate("/app/producers")} data-testid="compare-back" className="text-ink-600 flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> {t("nav.producers")}
        </button>
        <Card className="p-8 text-center">
          <p className="text-ink-700">{t("producers.compareEmpty")}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto animate-fade-up" data-testid="compare-page">
      <button onClick={() => navigate(-1)} data-testid="compare-back" className="text-ink-600 flex items-center gap-1 mb-4 hover:text-ink-900">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-baseline gap-3 mb-1">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-electric-600">R4Pro · Compare</p>
      </div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-ink-900">
        {candidates.length} candidates
      </h1>
      <p className="text-sm text-ink-600 mt-1 mb-6">
        Reputation · Compatibility · Response time · Lead time · Rate
      </p>

      <div className="grid grid-cols-[140px_repeat(var(--cols),1fr)] gap-3 mb-4" style={{ "--cols": candidates.length }}>
        <div />
        {candidates.map((p) => (
          <Card key={p.id} className="p-4" data-testid={`cmp-head-${p.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-lg bg-carbon-750 border border-carbon-600 flex items-center justify-center font-mono text-electric-600 text-sm shrink-0">
                {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-ink-900 truncate">{p.name}</div>
                <div className="flex items-center gap-1 text-[10px] text-ink-500 mt-0.5">
                  <StatusDot status={p.availability} />
                  {t(`availability.${p.availability}`)}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-ink-600 line-clamp-2 h-8">{p.positioning}</p>
            {p.verified && <div className="mt-2"><VerifiedBadge label={t("producer.verified")} /></div>}
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-[140px_repeat(var(--cols),1fr)] gap-3 mb-4" style={{ "--cols": candidates.length }}>
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-600 self-center flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Compatibility
        </div>
        {candidates.map((p) => (
          <div key={p.id} className="flex justify-center py-2" data-testid={`cmp-match-${p.id}`}>
            <CompatibilityRing score={p.compatibility} size={72} stroke={5} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-[140px_repeat(var(--cols),1fr)] gap-3 mb-6" style={{ "--cols": candidates.length }}>
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-600 self-center flex items-center gap-1">
          <Award className="w-3 h-3" /> {t("reputation.label")}
        </div>
        {candidates.map((p) => (
          <div key={p.id} className="flex justify-center py-2" data-testid={`cmp-rep-${p.id}`}>
            <div className="text-center">
              <div className="font-mono text-3xl font-bold text-[#8B5A0C]">{p.reputation}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-ink-500">
                {p.reputation >= 90 ? t("reputation.levels.expert") :
                 p.reputation >= 75 ? t("reputation.levels.established") :
                 p.reputation >= 60 ? t("reputation.levels.rising") :
                 t("reputation.levels.new")}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Card className="p-5">
        <Row label={t("producer.responseTime")} values={candidates.map((p) => `${p.responseTimeH}h`)} mono />
        <Row label={t("producer.responseRate")} values={candidates.map((p) => `${p.responseRate}%`)} mono />
        <Row label={t("producer.onTime")} values={candidates.map((p) => `${p.onTimeRate}%`)} mono />
        <Row label={t("producer.leadTime")} values={candidates.map((p) => `${p.leadTimeDays} days`)} mono />
        <Row label={t("producer.rate")} values={candidates.map((p) => `€ ${p.rateFrom}/h`)} mono />
        <Row label={t("producer.completed")} values={candidates.map((p) => `${p.projectsCompleted}`)} mono />
        <Row label={t("producer.yearsActive")} values={candidates.map((p) => `${p.yearsActive}`)} mono />
        <Row label={t("producer.location")} values={candidates.map((p) => (
          <span key="l" className="flex items-center gap-1 text-xs">
            <MapPin className="w-3 h-3 text-ink-500" /> {p.location || "—"}
          </span>
        ))} />
        <Row label={t("producer.certifications")} values={candidates.map((p) => (
          <div key="c" className="flex flex-wrap gap-1">
            {p.certifications.length === 0 ? <span className="text-ink-500 text-xs">—</span> : p.certifications.slice(0, 2).map((c) => <Chip key={c}>{c}</Chip>)}
          </div>
        ))} />
        <Row label={t("producer.skills")} values={candidates.map((p) => (
          <div key="s" className="flex flex-wrap gap-1">
            {p.skills.slice(0, 3).map((s) => <Chip key={s}>{s}</Chip>)}
          </div>
        ))} />
      </Card>

      <div className="grid grid-cols-[140px_repeat(var(--cols),1fr)] gap-3 mt-4" style={{ "--cols": candidates.length }}>
        <div />
        {candidates.map((p) => (
          <div key={p.id} className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate("/app/messages")} data-testid={`cmp-contact-${p.id}`} className="flex-1">
              <MessageSquare className="w-4 h-4" /> {t("producer.contact")}
            </Button>
            <Button onClick={() => navigate(`/app/producers/${p.id}`)} data-testid={`cmp-view-${p.id}`} className="flex-1">
              {t("producers.view")} <Send className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
